import {useState, useEffect, useCallback} from 'react';
import {useNavigate} from 'react-router-dom';
import {
    Box,
    Typography,
    TextField,
    InputAdornment,
    TablePagination,
    Paper,
    Chip,
    IconButton,
    Tooltip,
    MenuItem,
    Card,
    CardContent,
    Grid,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    CircularProgress,
    Alert,
    Divider,
    Stack,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    ToggleButton,
    ToggleButtonGroup,
    useMediaQuery,
} from '@mui/material';
import {useTheme} from '@mui/material/styles';
import {
    Search as SearchIcon,
    Download as DownloadIcon,
    Delete as DeleteIcon,
    Refresh as RefreshIcon,
    Clear as ClearIcon,
    Add as AddIcon,
    FiberManualRecord as DotIcon,
    Block as RevokeIcon,
    ContentCopy as CopyIcon,
    ExpandMore as ExpandMoreIcon,
    ViewList as ViewListIcon,
    Groups as GroupsIcon,
} from '@mui/icons-material';
import {toast} from 'react-toastify';
import {licenseApi} from 'utils/api';

const ACTION_TYPE_LABELS = {
    NEW: '최초 발급',
    RENEW: '신규 추가',
    CONVERT: '전환',
};

const STATUS_CONFIG = {
    ACTIVE: {label: '활성', color: 'success'},
    CONVERTED: {label: '전환', color: 'info'},
    REVOKED: {label: '파기', color: 'error'},
};

// datetime 문자열을 간략하게 표시 (YYYY-MM-DD HH:mm 또는 YYYY-MM-DD)
function formatValidityDisplay(dateStr) {
    if (!dateStr) return '';
    // ISO datetime 형식인 경우 (2026-02-05T14:30:00.000Z)
    if (dateStr.includes('T')) {
        const d = new Date(dateStr);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        const hour = String(d.getHours()).padStart(2, '0');
        const min = String(d.getMinutes()).padStart(2, '0');
        // 시간이 00:00이면 날짜만 표시
        if (hour === '00' && min === '00') {
            return `${year}-${month}-${day}`;
        }
        return `${year}-${month}-${day} ${hour}:${min}`;
    }
    // 기존 date-only 형식 (YYYY-MM-DD)
    return dateStr;
}

function getDaysRemaining(license) {
    if (!license.valid_until) return null;
    const now = new Date();
    const until = new Date(license.valid_until);
    return Math.ceil((until.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
}

function isPerpetual(license) {
    return license.license_type === 'PRODUCTION' && !license.valid_until;
}

function getExpirationTextColor(days) {
    if (days === null) return undefined;
    if (days <= 0) return '#f44336';
    if (days <= 7) return '#f44336';
    if (days <= 30) return '#ed6c02';
    return undefined;
}

function getTypeColor(type) {
    switch (type) {
        case 'DEV':
            return 'warning';
        case 'PRODUCTION':
            return 'success';
        default:
            return 'default';
    }
}

function getTypeLabel(type) {
    switch (type) {
        case 'DEV':
            return '개발';
        case 'PRODUCTION':
            return '정식';
        default:
            return type;
    }
}

function LicenseRow({license, isChainItem, showCustomer, onDetail, onDownload, onDelete, onRenew, isMobile}) {
    const daysRemaining = getDaysRemaining(license);
    const expirationColor = getExpirationTextColor(daysRemaining);

    const validityContent = isPerpetual(license) ? (
        <Chip label="영구" size="small" color="success" variant="outlined"/>
    ) : daysRemaining !== null && daysRemaining <= 0 ? (
        <Chip label="만료" size="small" color="error"/>
    ) : license.valid_until ? (
        <Typography variant="caption" noWrap sx={{color: expirationColor}}>
            {formatValidityDisplay(license.valid_from)} ~ {formatValidityDisplay(license.valid_until)}
            {daysRemaining !== null && ` (${daysRemaining}일)`}
        </Typography>
    ) : null;

    if (isMobile) {
        const infoText = [
            showCustomer && license.customer_name,
            license.project_name,
            license.product_name,
        ].filter(Boolean).join(' · ');

        return (
            <Box
                sx={{
                    px: 2, py: 1, cursor: 'pointer',
                    '&:hover': {bgcolor: 'action.hover'},
                    borderBottom: '1px solid', borderBottomColor: 'divider',
                    ...(isChainItem && {pl: 3}),
                }}
                onClick={() => onDetail(license)}
            >
                <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5}}>
                    {isChainItem && (
                        <Chip
                            label={ACTION_TYPE_LABELS[license.action_type] || '최초 발급'}
                            size="small" variant="outlined"
                            color={license.action_type === 'CONVERT' ? 'info' : license.action_type === 'RENEW' ? 'success' : 'default'}
                            sx={{fontSize: '0.65rem', height: 18}}
                        />
                    )}
                    <Chip
                        label={STATUS_CONFIG[license.status]?.label || license.status}
                        size="small"
                        color={STATUS_CONFIG[license.status]?.color || 'default'}
                        variant={license.status === 'ACTIVE' ? 'filled' : 'outlined'}
                    />
                    <Chip label={getTypeLabel(license.license_type)} size="small" color={getTypeColor(license.license_type)}/>
                    {validityContent}
                    <Box sx={{flexGrow: 1}}/>
                    <Box onClick={(e) => e.stopPropagation()} sx={{display: 'flex'}}>
                        {onRenew && !isChainItem && license.status === 'ACTIVE' && license.product_type !== 'EXTERNAL' && (
                            <IconButton size="small" color="primary" onClick={() => onRenew(license)}>
                                <AddIcon fontSize="small"/>
                            </IconButton>
                        )}
                        <IconButton size="small" onClick={() => onDownload(license)}>
                            <DownloadIcon fontSize="small"/>
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => onDelete(license)}>
                            <DeleteIcon fontSize="small"/>
                        </IconButton>
                    </Box>
                </Box>
                <Typography variant="body2" noWrap>{infoText}</Typography>
                <Typography variant="caption" color="text.secondary">
                    {new Date(license.created_at).toLocaleString('ko-KR', {
                        year: 'numeric', month: '2-digit', day: '2-digit',
                        hour: '2-digit', minute: '2-digit'
                    })}
                </Typography>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 0.75,
                cursor: 'pointer',
                '&:hover': {bgcolor: 'action.hover'},
                borderBottom: '1px solid',
                borderBottomColor: 'divider',
                ...(isChainItem && {pl: 3}),
            }}
            onClick={() => onDetail(license)}
        >
            {showCustomer && (
                <Tooltip title={license.customer_name} enterDelay={500}>
                    <Typography variant="body2" fontWeight="bold" noWrap sx={{flex: '1 1 0', minWidth: 0}}>
                        {license.customer_name}
                    </Typography>
                </Tooltip>
            )}
            {isChainItem && (
                <Chip
                    label={ACTION_TYPE_LABELS[license.action_type] || '최초 발급'}
                    size="small"
                    variant="outlined"
                    color={license.action_type === 'CONVERT' ? 'info' : license.action_type === 'RENEW' ? 'success' : 'default'}
                    sx={{fontSize: '0.7rem', height: 20, flexShrink: 0}}
                />
            )}
            <Tooltip title={license.project_name || '-'} enterDelay={500}>
                <Typography variant="body2" noWrap sx={{flex: '1.2 1 0', minWidth: 0}}>
                    {license.project_name || '-'}
                </Typography>
            </Tooltip>
            <Tooltip title={license.product_name || '-'} enterDelay={500}>
                <Typography variant="body2" color="text.secondary" noWrap sx={{flex: '0.8 1 0', minWidth: 0}}>
                    {license.product_name || '-'}
                </Typography>
            </Tooltip>
            <Box sx={{flex: '1.5 1 0', minWidth: 0}}>
                {validityContent}
            </Box>
            <Box sx={{flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1}}>
                <Chip
                    label={STATUS_CONFIG[license.status]?.label || license.status}
                    size="small"
                    color={STATUS_CONFIG[license.status]?.color || 'default'}
                    variant={license.status === 'ACTIVE' ? 'filled' : 'outlined'}
                />
                <Chip label={getTypeLabel(license.license_type)} size="small" color={getTypeColor(license.license_type)}/>
                <Typography variant="caption" color="text.secondary" sx={{minWidth: 110, textAlign: 'right'}}>
                    {new Date(license.created_at).toLocaleString('ko-KR', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit'
                    })}
                </Typography>
                <Box onClick={(e) => e.stopPropagation()} sx={{display: 'flex'}}>
                    <Tooltip title="다운로드">
                        <IconButton size="small" onClick={() => onDownload(license)}>
                            <DownloadIcon fontSize="small"/>
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="삭제">
                        <IconButton size="small" color="error" onClick={() => onDelete(license)}>
                            <DeleteIcon fontSize="small"/>
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>
        </Box>
    );
}

function buildChains(licenses) {
    const chainMap = new Map();
    licenses.forEach((license) => {
        const key = license.root_id || license.id;
        if (!chainMap.has(key)) chainMap.set(key, []);
        chainMap.get(key).push(license);
    });
    return Array.from(chainMap.values())
        .map(items => items.sort((a, b) => a.created_at.localeCompare(b.created_at)))
        .sort((a, b) => b[b.length - 1].created_at.localeCompare(a[a.length - 1].created_at));
}

function ChainAccordion({chain, onDetail, onDownload, onDelete, onRenew, showCustomer, isMobile}) {
    const latest = chain[chain.length - 1];
    const isChain = chain.length > 1;
    const daysRemaining = getDaysRemaining(latest);
    const expirationColor = getExpirationTextColor(daysRemaining);
    const isActive = latest.status === 'ACTIVE';
    const isInternal = latest.product_type !== 'EXTERNAL';

    const validityContent = isPerpetual(latest) ? (
        <Chip label="영구" size="small" color="success" variant="outlined"/>
    ) : daysRemaining !== null && daysRemaining <= 0 ? (
        <Chip label="만료" size="small" color="error"/>
    ) : latest.valid_until ? (
        <Typography variant="caption" noWrap sx={{color: expirationColor}}>
            {formatValidityDisplay(latest.valid_from)} ~ {formatValidityDisplay(latest.valid_until)}
            {daysRemaining !== null && ` (${daysRemaining}일)`}
        </Typography>
    ) : null;

    if (!isChain) {
        return (
            <LicenseRow
                license={latest}
                showCustomer={showCustomer}
                onDetail={onDetail}
                onDownload={onDownload}
                onDelete={onDelete}
                onRenew={onRenew}
                isMobile={isMobile}
            />
        );
    }

    // 모바일 체인 아코디언
    if (isMobile) {
        const infoText = [
            showCustomer && latest.customer_name,
            latest.project_name,
            latest.product_name,
        ].filter(Boolean).join(' · ');

        return (
            <Accordion
                variant="outlined"
                sx={{
                    '&:before': {display: 'none'},
                    borderLeft: '3px solid',
                    borderLeftColor: 'primary.light',
                }}
                disableGutters
            >
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon fontSize="small"/>}
                    sx={{px: 2, py: 0.5, '& .MuiAccordionSummary-content': {my: 0, display: 'block'}}}
                >
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5, flexWrap: 'wrap'}}>
                        <Chip label={`${chain.length}건`} size="small" variant="outlined" sx={{fontSize: '0.65rem', height: 20}}/>
                        <Chip
                            label={STATUS_CONFIG[latest.status]?.label || latest.status}
                            size="small"
                            color={STATUS_CONFIG[latest.status]?.color || 'default'}
                            variant={latest.status === 'ACTIVE' ? 'filled' : 'outlined'}
                        />
                        <Chip label={getTypeLabel(latest.license_type)} size="small" color={getTypeColor(latest.license_type)}/>
                        {validityContent}
                        <Box sx={{flexGrow: 1}}/>
                        <Box onClick={(e) => e.stopPropagation()} sx={{display: 'flex'}}>
                            {isActive && isInternal && (
                                <IconButton size="small" color="primary" onClick={() => onRenew(latest)}>
                                    <AddIcon fontSize="small"/>
                                </IconButton>
                            )}
                            <IconButton size="small" onClick={() => onDownload(latest)}>
                                <DownloadIcon fontSize="small"/>
                            </IconButton>
                        </Box>
                    </Box>
                    <Typography variant="body2" noWrap>{infoText}</Typography>
                    <Typography variant="caption" color="text.secondary">
                        {new Date(latest.created_at).toLocaleString('ko-KR', {
                            year: 'numeric', month: '2-digit', day: '2-digit',
                            hour: '2-digit', minute: '2-digit'
                        })}
                    </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{p: 0}}>
                    {chain.map((license) => (
                        <LicenseRow
                            key={license.id}
                            license={license}
                            isChainItem
                            isMobile
                            onDetail={onDetail}
                            onDownload={onDownload}
                            onDelete={onDelete}
                            onRenew={onRenew}
                        />
                    ))}
                </AccordionDetails>
            </Accordion>
        );
    }

    // 데스크톱 체인 아코디언
    return (
        <Accordion
            variant="outlined"
            sx={{
                '&:before': {display: 'none'},
                borderLeft: '3px solid',
                borderLeftColor: 'primary.light',
            }}
            disableGutters
        >
            <AccordionSummary
                expandIcon={<ExpandMoreIcon fontSize="small"/>}
                sx={{minHeight: 40, px: 2, '& .MuiAccordionSummary-content': {my: 0.5}}}
            >
                <Box sx={{display: 'flex', alignItems: 'center', gap: 1, width: '100%', pr: 1}}>
                    {showCustomer && (
                        <Tooltip title={latest.customer_name} enterDelay={500}>
                            <Typography variant="body2" fontWeight="bold" noWrap sx={{flex: '1 1 0', minWidth: 0}}>
                                {latest.customer_name}
                            </Typography>
                        </Tooltip>
                    )}
                    <Tooltip title={latest.project_name || '-'} enterDelay={500}>
                        <Typography variant="body2" fontWeight="medium" noWrap sx={{flex: '1.2 1 0', minWidth: 0}}>
                            {latest.project_name || '-'}
                        </Typography>
                    </Tooltip>
                    <Tooltip title={latest.product_name || '-'} enterDelay={500}>
                        <Typography variant="body2" color="text.secondary" noWrap sx={{flex: '0.8 1 0', minWidth: 0}}>
                            {latest.product_name || '-'}
                        </Typography>
                    </Tooltip>
                    <Box sx={{flex: '1.5 1 0', minWidth: 0}}>
                        {validityContent}
                    </Box>
                    <Box sx={{flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1}}>
                        <Chip label={`${chain.length}건`} size="small" variant="outlined" sx={{fontSize: '0.7rem'}}/>
                        <Chip
                            label={STATUS_CONFIG[latest.status]?.label || latest.status}
                            size="small"
                            color={STATUS_CONFIG[latest.status]?.color || 'default'}
                            variant={latest.status === 'ACTIVE' ? 'filled' : 'outlined'}
                        />
                        <Chip label={getTypeLabel(latest.license_type)} size="small"
                              color={getTypeColor(latest.license_type)}/>
                        <Typography variant="caption" color="text.secondary" sx={{minWidth: 110, textAlign: 'right'}}>
                            {new Date(latest.created_at).toLocaleString('ko-KR', {
                                year: 'numeric',
                                month: '2-digit',
                                day: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit'
                            })}
                        </Typography>
                        <Box onClick={(e) => e.stopPropagation()} sx={{display: 'flex'}}>
                            <Tooltip title="다운로드">
                                <IconButton size="small" onClick={() => onDownload(latest)}>
                                    <DownloadIcon fontSize="small"/>
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Box>
                </Box>
            </AccordionSummary>
            <AccordionDetails sx={{p: 0}}>
                {chain.map((license) => (
                    <LicenseRow
                        key={license.id}
                        license={license}
                        isChainItem
                        onDetail={onDetail}
                        onDownload={onDownload}
                        onDelete={onDelete}
                        onRenew={onRenew}
                    />
                ))}
            </AccordionDetails>
        </Accordion>
    );
}

export default function MainPage() {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [licenses, setLicenses] = useState([]);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [searchQuery, setSearchQuery] = useState('');
    const [filterType, setFilterType] = useState('');
    const [filterProduct, setFilterProduct] = useState('');
    const [statsFilter, setStatsFilter] = useState('');

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [total, setTotal] = useState(0);

    const [stats, setStats] = useState(null);

    const [selectedLicense, setSelectedLicense] = useState(null);
    const [detailOpen, setDetailOpen] = useState(false);
    const [history, setHistory] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);

    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [revokeOpen, setRevokeOpen] = useState(false);

    // 뷰모드: 'list' | 'grouped'
    const [viewMode, setViewMode] = useState('grouped');
    const [groups, setGroups] = useState([]);
    const [groupTotal, setGroupTotal] = useState(0);
    const [groupPage, setGroupPage] = useState(0);
    const [groupRowsPerPage, setGroupRowsPerPage] = useState(10);
    const [groupLoading, setGroupLoading] = useState(false);
    const [expandedCustomer, setExpandedCustomer] = useState(null);

    const hasActiveFilters = filterType || filterProduct || statsFilter;

    useEffect(() => {
        loadProducts();
    }, []);

    const loadProducts = async () => {
        try {
            const response = await licenseApi.getProducts();
            setProducts(response.data.products || []);
        } catch (err) {
            // ignore
        }
    };

    const loadLicenses = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const params = {
                page: page + 1,
                limit: rowsPerPage,
                sortBy: 'created_at',
                sortOrder: 'desc',
            };

            if (searchQuery) params.query = searchQuery;
            if (filterType) params.licenseType = filterType;
            if (filterProduct) params.productId = filterProduct;
            if (statsFilter === 'thisMonth' || statsFilter === 'expiringSoon') params.period = statsFilter;
            if (statsFilter === 'production') params.licenseType = 'PRODUCTION';

            const response = await licenseApi.list(params);
            setLicenses(response.data.data);
            setTotal(response.data.total);
        } catch (err) {
            setError('데이터를 불러오는데 실패했습니다.');
        } finally {
            setLoading(false);
        }
    }, [page, rowsPerPage, searchQuery, filterType, filterProduct, statsFilter]);

    const loadStats = useCallback(async () => {
        try {
            const response = await licenseApi.stats();
            setStats(response.data);
        } catch (err) {
            console.error('Failed to load stats:', err);
        }
    }, []);

    useEffect(() => {
        loadLicenses();
        loadStats();
    }, [loadLicenses, loadStats]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setPage(0);
            loadLicenses();
        }, 300);
        return () => clearTimeout(timer);
    }, [searchQuery, filterType, filterProduct, statsFilter]);

    const loadGrouped = useCallback(async () => {
        setGroupLoading(true);
        try {
            const params = {
                page: groupPage + 1,
                limit: groupRowsPerPage,
            };
            if (searchQuery) params.query = searchQuery;
            if (filterType) params.licenseType = filterType;
            if (filterProduct) params.productId = filterProduct;
            if (statsFilter === 'thisMonth' || statsFilter === 'expiringSoon') params.period = statsFilter;
            if (statsFilter === 'production') params.licenseType = 'PRODUCTION';

            const response = await licenseApi.listGrouped(params);
            setGroups(response.data.groups);
            setGroupTotal(response.data.total);
        } catch (err) {
            setError('데이터를 불러오는데 실패했습니다.');
        } finally {
            setGroupLoading(false);
        }
    }, [groupPage, groupRowsPerPage, searchQuery, filterType, filterProduct, statsFilter]);

    useEffect(() => {
        if (viewMode === 'grouped') {
            loadGrouped();
        }
    }, [viewMode, loadGrouped]);

    useEffect(() => {
        if (viewMode === 'grouped') {
            const timer = setTimeout(() => {
                setGroupPage(0);
                loadGrouped();
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [searchQuery, filterType, filterProduct, statsFilter, viewMode]);

    const handleClearFilters = () => {
        setFilterType('');
        setFilterProduct('');
        setSearchQuery('');
    };

    const handleDownload = async (license) => {
        try {
            const response = await licenseApi.download(license.id);
            const disposition = response.headers['content-disposition'];
            let filename = `license-${license.customer_name}-${license.id.substring(0, 8)}.lic`;
            if (disposition) {
                const match = disposition.match(/filename[^;=\n]*=["']?([^"';\n]*)["']?/);
                if (match?.[1]) filename = decodeURIComponent(match[1]);
            }
            const blob = new Blob([response.data]);
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            a.click();
            URL.revokeObjectURL(url);
            toast.success('다운로드 완료');
        } catch (err) {
            toast.error('다운로드 실패');
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;

        try {
            await licenseApi.delete(deleteTarget.id);
            toast.success('라이선스가 삭제되었습니다.');
            setDeleteOpen(false);
            setDeleteTarget(null);
            await loadLicenses();
            await loadStats();
        } catch (err) {
            toast.error(err.response?.data?.error || '삭제 실패');
        }
    };

    const handleOpenDetail = async (license) => {
        setSelectedLicense(license);
        setDetailOpen(true);
        setHistory([]);
        setHistoryLoading(true);
        try {
            const response = await licenseApi.getHistory(license.id);
            setHistory(response.data.history || []);
        } catch (err) {
            // ignore - history is supplementary
        } finally {
            setHistoryLoading(false);
        }
    };

    const handleRenew = (license) => {
        setDetailOpen(false);
        navigate(`/generate?from=${license.id}&action=renew`);
    };

    const handleRevoke = async () => {
        if (!selectedLicense) return;
        try {
            await licenseApi.updateStatus(selectedLicense.id, 'REVOKED');
            toast.success('라이선스가 파기되었습니다.');
            setRevokeOpen(false);
            setDetailOpen(false);
            loadLicenses();
            loadStats();
        } catch (err) {
            toast.error(err.response?.data?.error || '상태 변경 실패');
        }
    };

    return (
        <Box>
            <Typography variant="h6" gutterBottom>
                발급 목록
            </Typography>

            {/* 통계 카드 */}
            {stats && (
                <Grid container spacing={2} sx={{mb: 3}}>
                    {[
                        {key: '', label: '전체 발급', value: stats.total, color: 'primary'},
                        {key: 'thisMonth', label: '이번 달', value: stats.thisMonth, color: 'success.main'},
                        {key: 'expiringSoon', label: '만료 임박', value: stats.expiringSoon, color: 'warning.main'},
                        {key: 'production', label: '정식 라이선스', value: stats.byType?.['PRODUCTION'] || 0, color: 'text.primary'},
                    ].map((item) => (
                        <Grid item xs={6} md={3} key={item.key}>
                            <Card
                                variant="outlined"
                                onClick={() => setStatsFilter(statsFilter === item.key ? '' : item.key)}
                                sx={{
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    ...(statsFilter === item.key && {
                                        borderColor: 'primary.main',
                                        bgcolor: 'primary.50',
                                        boxShadow: 1,
                                    }),
                                    '&:hover': {bgcolor: statsFilter === item.key ? 'primary.50' : 'action.hover'},
                                }}
                            >
                                <CardContent sx={{textAlign: 'center', py: 1.5}}>
                                    <Typography variant="h4" color={item.color}>{item.value}</Typography>
                                    <Typography variant="body2" color="text.secondary">{item.label}</Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )}

            {/* 검색 및 필터 */}
            <Box sx={{display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap'}}>
                <TextField
                    placeholder="고객사, 사업명으로 검색..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    sx={{flexGrow: 1, minWidth: {xs: '100%', sm: 200}}}
                    size="small"
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon/>
                            </InputAdornment>
                        ),
                    }}
                />
                <TextField
                    select
                    label="제품"
                    value={filterProduct}
                    onChange={(e) => setFilterProduct(e.target.value)}
                    size="small"
                    sx={{minWidth: {xs: 0, sm: 140}, flexGrow: {xs: 1, sm: 0}}}
                >
                    <MenuItem value="">전체</MenuItem>
                    {products.map((p) => (
                        <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                    ))}
                </TextField>
                <TextField
                    select
                    label="구분"
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                    size="small"
                    sx={{minWidth: {xs: 0, sm: 120}, flexGrow: {xs: 1, sm: 0}}}
                >
                    <MenuItem value="">전체</MenuItem>
                    <MenuItem value="DEV">개발</MenuItem>
                    <MenuItem value="PRODUCTION">정식</MenuItem>
                </TextField>
                {hasActiveFilters && (
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={handleClearFilters}
                        startIcon={<ClearIcon/>}
                    >
                        초기화
                    </Button>
                )}
                <ToggleButtonGroup
                    value={viewMode}
                    exclusive
                    onChange={(_, v) => {
                        if (v) setViewMode(v);
                    }}
                    size="small"
                >
                    <ToggleButton value="list">
                        <Tooltip title="목록"><ViewListIcon fontSize="small"/></Tooltip>
                    </ToggleButton>
                    <ToggleButton value="grouped">
                        <Tooltip title="고객사별"><GroupsIcon fontSize="small"/></Tooltip>
                    </ToggleButton>
                </ToggleButtonGroup>
                <Tooltip title="새로고침">
                    <IconButton onClick={() => {
                        viewMode === 'grouped' ? loadGrouped() : loadLicenses();
                        loadStats();
                    }}>
                        <RefreshIcon/>
                    </IconButton>
                </Tooltip>
            </Box>

            {error && (
                <Alert severity="error" sx={{mb: 2}}>
                    {error}
                </Alert>
            )}

            {/* 고객사별 그룹 뷰 */}
            {viewMode === 'grouped' && (
                <Box>
                    {groupLoading ? (
                        <Box sx={{display: 'flex', justifyContent: 'center', py: 4}}>
                            <CircularProgress size={32}/>
                        </Box>
                    ) : groups.length === 0 ? (
                        <Paper variant="outlined" sx={{py: 4, textAlign: 'center'}}>
                            <Typography color="text.secondary">
                                {searchQuery || hasActiveFilters ? '검색 결과가 없습니다.' : '발급된 라이선스가 없습니다.'}
                            </Typography>
                        </Paper>
                    ) : (
                        groups.map((group) => (
                            <Accordion
                                key={group.customer_name}
                                expanded={expandedCustomer === group.customer_name}
                                onChange={(_, expanded) => setExpandedCustomer(expanded ? group.customer_name : null)}
                                variant="outlined"
                                sx={{mb: 0.5}}
                            >
                                <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
                                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5, width: '100%', pr: 2}}>
                                        <Typography variant="subtitle2" fontWeight="bold" sx={{minWidth: 120}}>
                                            {group.customer_name}
                                        </Typography>
                                        <Chip label={`${group.license_count}건`} size="small" variant="outlined"/>
                                        {group.active_count > 0 && (
                                            <Chip label={`활성 ${group.active_count}`} size="small" color="success"
                                                  variant="outlined"/>
                                        )}
                                        {group.prod_count > 0 && (
                                            <Chip label={`정식 ${group.prod_count}`} size="small" color="success"/>
                                        )}
                                        {group.dev_count > 0 && (
                                            <Chip label={`개발 ${group.dev_count}`} size="small" color="warning"/>
                                        )}
                                        <Typography variant="caption" color="text.secondary" sx={{ml: 'auto'}}>
                                            최근 {new Date(group.latest_created_at).toLocaleString('ko-KR', {
                                            year: 'numeric',
                                            month: '2-digit',
                                            day: '2-digit'
                                        })}
                                        </Typography>
                                        <Tooltip title="신규 발급">
                                            <IconButton
                                                size="small"
                                                color="primary"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    navigate(`/generate?customer=${encodeURIComponent(group.customer_name)}`);
                                                }}
                                            >
                                                <AddIcon fontSize="small"/>
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </AccordionSummary>
                                <AccordionDetails sx={{p: 0}}>
                                    {buildChains(group.licenses).map((chain) => (
                                        <ChainAccordion
                                            key={chain[chain.length - 1].root_id || chain[chain.length - 1].id}
                                            chain={chain}
                                            isMobile={isMobile}
                                            onDetail={handleOpenDetail}
                                            onDownload={handleDownload}
                                            onDelete={(lic) => {
                                                setDeleteTarget(lic);
                                                setDeleteOpen(true);
                                            }}
                                            onRenew={handleRenew}
                                        />
                                    ))}
                                </AccordionDetails>
                            </Accordion>
                        ))
                    )}
                    <TablePagination
                        component="div"
                        count={groupTotal}
                        page={groupPage}
                        onPageChange={(_, newPage) => setGroupPage(newPage)}
                        rowsPerPage={groupRowsPerPage}
                        onRowsPerPageChange={(e) => {
                            setGroupRowsPerPage(parseInt(e.target.value, 10));
                            setGroupPage(0);
                        }}
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        labelRowsPerPage="페이지당 고객사:"
                        labelDisplayedRows={({from, to, count}) => `${from}-${to} / ${count}`}
                    />
                </Box>
            )}

            {/* 목록 뷰 (체인 아코디언) */}
            {viewMode === 'list' && (
                <Box>
                    {loading ? (
                        <Box sx={{display: 'flex', justifyContent: 'center', py: 4}}>
                            <CircularProgress size={32}/>
                        </Box>
                    ) : licenses.length === 0 ? (
                        <Paper variant="outlined" sx={{py: 4, textAlign: 'center'}}>
                            <Typography color="text.secondary">
                                {searchQuery || hasActiveFilters ? '검색 결과가 없습니다.' : '발급된 라이선스가 없습니다.'}
                            </Typography>
                        </Paper>
                    ) : (
                        <Paper variant="outlined">{!isMobile && (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        px: 2,
                                        py: 0.75,
                                        borderBottom: '2px solid',
                                        borderBottomColor: 'divider',
                                    }}
                                >
                                    <Typography variant="caption" fontWeight="bold" color="text.secondary"
                                                sx={{flex: '1 1 0', minWidth: 0}}>고객사</Typography>
                                    <Typography variant="caption" fontWeight="bold" color="text.secondary"
                                                sx={{flex: '1.2 1 0', minWidth: 0}}>사업명</Typography>
                                    <Typography variant="caption" fontWeight="bold" color="text.secondary"
                                                sx={{flex: '0.8 1 0', minWidth: 0}}>제품명</Typography>
                                    <Typography variant="caption" fontWeight="bold" color="text.secondary"
                                                sx={{flex: '1.5 1 0', minWidth: 0}}>유효기간</Typography>
                                    <Box sx={{
                                        flexGrow: 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'flex-end',
                                        gap: 1
                                    }}>
                                        <Typography variant="caption" fontWeight="bold"
                                                    color="text.secondary">상태</Typography>
                                        <Typography variant="caption" fontWeight="bold"
                                                    color="text.secondary">구분</Typography>
                                        <Typography variant="caption" fontWeight="bold" color="text.secondary"
                                                    sx={{minWidth: 110, textAlign: 'right'}}>발급일시</Typography>
                                        <Box sx={{display: 'flex', minWidth: 102, justifyContent: 'center'}}>
                                            <Typography variant="caption" fontWeight="bold"
                                                        color="text.secondary">작업</Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            )}
                            {buildChains(licenses).map((chain) => (
                                <ChainAccordion
                                    key={chain[chain.length - 1].root_id || chain[chain.length - 1].id}
                                    chain={chain}
                                    showCustomer
                                    isMobile={isMobile}
                                    onDetail={handleOpenDetail}
                                    onDownload={handleDownload}
                                    onDelete={(lic) => {
                                        setDeleteTarget(lic);
                                        setDeleteOpen(true);
                                    }}
                                    onRenew={handleRenew}
                                />
                            ))}
                        </Paper>
                    )}
                    <TablePagination
                        component="div"
                        count={total}
                        page={page}
                        onPageChange={(_, newPage) => setPage(newPage)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={(e) => {
                            setRowsPerPage(parseInt(e.target.value, 10));
                            setPage(0);
                        }}
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        labelRowsPerPage="페이지당 행:"
                        labelDisplayedRows={({from, to, count}) => `${from}-${to} / ${count}`}
                    />
                </Box>
            )}

            {/* 상세 보기 다이얼로그 */}
            <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>라이선스 상세 정보</DialogTitle>
                <DialogContent dividers>
                    {selectedLicense && (
                        <Box>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle2" color="text.secondary">라이선스 ID</Typography>
                                    <Typography variant="body1" sx={{mb: 2}}>{selectedLicense.id}</Typography>

                                    <Typography variant="subtitle2" color="text.secondary">제품명</Typography>
                                    <Typography variant="body1"
                                                sx={{mb: 2}}>{selectedLicense.product_name || '-'}</Typography>

                                    <Typography variant="subtitle2" color="text.secondary">라이선스 구분 / 상태</Typography>
                                    <Box sx={{display: 'flex', gap: 1, mb: 2}}>
                                        <Chip label={getTypeLabel(selectedLicense.license_type)}
                                              color={getTypeColor(selectedLicense.license_type)}/>
                                        <Chip
                                            label={STATUS_CONFIG[selectedLicense.status]?.label || selectedLicense.status}
                                            color={STATUS_CONFIG[selectedLicense.status]?.color || 'default'}
                                            variant={selectedLicense.status === 'ACTIVE' ? 'filled' : 'outlined'}
                                        />
                                    </Box>

                                    <Typography variant="subtitle2" color="text.secondary">고객사</Typography>
                                    <Typography variant="body1"
                                                sx={{mb: 2}}>{selectedLicense.customer_name}</Typography>

                                    <Typography variant="subtitle2" color="text.secondary">사업명</Typography>
                                    <Typography variant="body1"
                                                sx={{mb: 2}}>{selectedLicense.project_name || '-'}</Typography>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle2" color="text.secondary">유효 기간</Typography>
                                    <Typography variant="body1" sx={{mb: 2}}>
                                        {isPerpetual(selectedLicense)
                                            ? '영구 라이선스'
                                            : `${formatValidityDisplay(selectedLicense.valid_from) || '-'} ~ ${formatValidityDisplay(selectedLicense.valid_until) || '-'}`}
                                    </Typography>

                                    {selectedLicense.product_type !== 'EXTERNAL' && (
                                        <>
                                            <Typography variant="subtitle2" color="text.secondary">코어 제한</Typography>
                                            <Typography variant="body1" sx={{mb: 2}}>
                                                {selectedLicense.max_cores === 0 ? '무제한' : `${selectedLicense.max_cores} cores`}
                                            </Typography>

                                            <Typography variant="subtitle2" color="text.secondary">도메인 정책</Typography>
                                            <Typography variant="body1" sx={{mb: 2}}>
                                                {selectedLicense.domain_policy}
                                                {selectedLicense.allowed_domains && ` (${selectedLicense.allowed_domains})`}
                                            </Typography>

                                            <Typography variant="subtitle2" color="text.secondary">바인딩 모드</Typography>
                                            <Typography variant="body1" sx={{mb: 2}}>
                                                {selectedLicense.binding_mode || 'NONE'}
                                            </Typography>
                                        </>
                                    )}
                                </Grid>

                                {/* 자사제품: 라이선스 파일 내용 */}
                                {selectedLicense.product_type !== 'EXTERNAL' && (
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2" color="text.secondary">라이선스 파일</Typography>
                                        <TextField
                                            fullWidth
                                            multiline
                                            rows={6}
                                            value={selectedLicense.license_content}
                                            InputProps={{readOnly: true}}
                                            sx={{fontFamily: 'monospace', fontSize: '0.75rem', mt: 1}}
                                        />
                                    </Grid>
                                )}

                                {/* 기타제품: 라이선스 키/파일 */}
                                {selectedLicense.product_type === 'EXTERNAL' && (
                                    <Grid item xs={12}>
                                        {selectedLicense.license_key && (
                                            <>
                                                <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 0.5}}>
                                                    <Typography variant="subtitle2" color="text.secondary">라이선스
                                                        키</Typography>
                                                    <Tooltip title="복사">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => {
                                                                navigator.clipboard.writeText(selectedLicense.license_key);
                                                                toast.success('클립보드에 복사되었습니다.');
                                                            }}
                                                        >
                                                            <CopyIcon fontSize="small"/>
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                                <TextField
                                                    fullWidth
                                                    multiline
                                                    rows={4}
                                                    value={selectedLicense.license_key}
                                                    InputProps={{readOnly: true}}
                                                    sx={{fontFamily: 'monospace', fontSize: '0.75rem', mb: 2}}
                                                />
                                            </>
                                        )}
                                        {selectedLicense.license_file_name && (
                                            <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                                <Typography variant="subtitle2" color="text.secondary">라이선스
                                                    파일:</Typography>
                                                <Chip label={selectedLicense.license_file_name} variant="outlined"
                                                      size="small"/>
                                                <Button
                                                    size="small"
                                                    startIcon={<DownloadIcon/>}
                                                    onClick={() => handleDownload(selectedLicense)}
                                                >
                                                    다운로드
                                                </Button>
                                            </Box>
                                        )}
                                        {!selectedLicense.license_key && !selectedLicense.license_file_name && (
                                            <Typography variant="body2" color="text.secondary">
                                                등록된 라이선스 정보가 없습니다.
                                            </Typography>
                                        )}
                                    </Grid>
                                )}
                            </Grid>

                            {/* 히스토리 */}
                            {history.length > 1 && (
                                <Box sx={{mt: 3}}>
                                    <Divider sx={{mb: 2}}/>
                                    <Typography variant="subtitle2" color="primary" gutterBottom>
                                        발급 이력
                                    </Typography>
                                    <Stack spacing={0.5}>
                                        {history.map((item) => (
                                            <Box
                                                key={item.id}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1.5,
                                                    py: 0.75,
                                                    px: 1.5,
                                                    borderRadius: 1,
                                                    cursor: item.id !== selectedLicense.id ? 'pointer' : 'default',
                                                    bgcolor: item.id === selectedLicense.id ? 'action.selected' : 'transparent',
                                                    '&:hover': item.id !== selectedLicense.id ? {bgcolor: 'action.hover'} : {},
                                                }}
                                                onClick={() => {
                                                    if (item.id !== selectedLicense.id) handleOpenDetail(item);
                                                }}
                                            >
                                                <DotIcon
                                                    sx={{
                                                        fontSize: 10,
                                                        color: item.id === selectedLicense.id ? 'primary.main' : 'text.disabled',
                                                    }}
                                                />
                                                <Typography variant="body2" color="text.secondary" sx={{minWidth: 85}}>
                                                    {new Date(item.created_at).toLocaleString('ko-KR', {
                                                        year: 'numeric',
                                                        month: '2-digit',
                                                        day: '2-digit',
                                                        hour: '2-digit',
                                                        minute: '2-digit'
                                                    })}
                                                </Typography>
                                                <Chip
                                                    label={ACTION_TYPE_LABELS[item.action_type] || '최초 발급'}
                                                    size="small"
                                                    variant={item.action_type === 'CONVERT' ? 'filled' : 'outlined'}
                                                    color={item.action_type === 'CONVERT' ? 'info' : item.action_type === 'RENEW' ? 'success' : 'default'}
                                                    sx={{minWidth: 70}}
                                                />
                                                <Chip
                                                    label={getTypeLabel(item.license_type)}
                                                    size="small"
                                                    color={getTypeColor(item.license_type)}
                                                />
                                                <Chip
                                                    label={STATUS_CONFIG[item.status]?.label || item.status}
                                                    size="small"
                                                    color={STATUS_CONFIG[item.status]?.color || 'default'}
                                                    variant="outlined"
                                                />
                                                <Typography variant="body2" sx={{flexGrow: 1}} noWrap>
                                                    {item.product_name}
                                                    {item.memo && ` - ${item.memo}`}
                                                </Typography>
                                                {isPerpetual(item) ? (
                                                    <Typography variant="caption" color="success.main">영구</Typography>
                                                ) : item.valid_until ? (
                                                    <Typography variant="caption" color="text.secondary">
                                                        ~{formatValidityDisplay(item.valid_until)}
                                                    </Typography>
                                                ) : null}
                                            </Box>
                                        ))}
                                    </Stack>
                                </Box>
                            )}

                            {historyLoading && (
                                <Box sx={{mt: 2, textAlign: 'center'}}>
                                    <CircularProgress size={20}/>
                                </Box>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{flexWrap: 'wrap', gap: 1, justifyContent: {xs: 'center', sm: 'flex-end'}}}>
                    <Button onClick={() => setDetailOpen(false)}>닫기</Button>
                    {selectedLicense && (
                        <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent="center" useFlexGap>
                            {selectedLicense.status === 'ACTIVE' && (
                                <Button
                                    variant="outlined"
                                    color="error"
                                    size="small"
                                    startIcon={<RevokeIcon/>}
                                    onClick={() => setRevokeOpen(true)}
                                >
                                    파기
                                </Button>
                            )}
                            <Button variant="contained" size="small" onClick={() => handleDownload(selectedLicense)}>
                                다운로드
                            </Button>
                        </Stack>
                    )}
                </DialogActions>
            </Dialog>

            {/* 파기 확인 다이얼로그 */}
            <Dialog open={revokeOpen} onClose={() => setRevokeOpen(false)}>
                <DialogTitle>라이선스 파기</DialogTitle>
                <DialogContent>
                    <Typography>
                        {selectedLicense?.customer_name} - {selectedLicense?.project_name} 의 라이선스를 파기하시겠습니까?
                    </Typography>
                    <Typography variant="body2" color="error" sx={{mt: 1}}>
                        파기된 라이선스는 더 이상 사용할 수 없습니다.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRevokeOpen(false)}>취소</Button>
                    <Button variant="contained" color="error" onClick={handleRevoke}>
                        파기
                    </Button>
                </DialogActions>
            </Dialog>

            {/* 삭제 확인 다이얼로그 */}
            <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
                <DialogTitle>라이선스 삭제</DialogTitle>
                <DialogContent>
                    <Typography>
                        {deleteTarget?.customer_name} - {deleteTarget?.project_name} 의 라이선스를 삭제하시겠습니까?
                    </Typography>
                    <Typography variant="body2" color="error" sx={{mt: 1}}>
                        이 작업은 되돌릴 수 없습니다.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteOpen(false)}>취소</Button>
                    <Button variant="contained" color="error" onClick={handleDelete}>
                        삭제
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
